import { omit, uniqBy } from 'lodash'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import ErrorComponent from '@/components/ErrorComponent'
import Loading from '@/components/Loading'
import { useAppSelector } from '@/store/hooks'
import { getErrors } from '@/store/selectors'

import messages from './messages'

// TODO Update the navbar to use grid so we can remove this 100vh height (navbar is doing the same)
// This can cause issues when used on an iPad with the dynamic sizing of the url bar
const StyledPageWrapper = styled.main`
  display: grid;
  background-color: white;
  grid-template-rows: minmax(65px, auto) 1fr;
  min-height: 100vh;
  width: 100%;

  h1 {
    color: #4b4b4b;
    font-size: 1.5rem;
    font-weight: normal;
    margin: 0;
  }

  p {
    font-size: 0.9rem;
  }
`

const StyledContent = styled.div`
  border-top: 1px solid #d9d9d9;
`

const matchesFunction = (sourceObj: any, otherObj: any) => {
  return Object.keys(sourceObj).reduce(
    (isMatch, prop) =>
      isMatch && ['array', 'object'].includes(typeof sourceObj[prop])
        ? sourceObj[prop].includes(otherObj[prop])
        : sourceObj[prop] === otherObj[prop],
    true
  )
}

const handleErrors = (errorsToHandle: Array<HandledError>, errors: Array<any>): ReactNode => {
  if (!errorsToHandle || errorsToHandle.length === 0 || !errors || errors.length === 0) return null

  const errorsWithIndex = errors.map((e, index) => omit({ ...e, errorMessage: e.message, index }, 'message'))
  const filteredErrors = uniqBy(
    errorsToHandle.flatMap(errorToHandle =>
      errorsWithIndex
        .filter(e => matchesFunction(omit(errorToHandle, 'data'), e))
        .map(e => ({
          ...e,
          ...errorToHandle.data
        }))
    ),
    'index'
  )

  return filteredErrors.length > 0 ? <ErrorComponent errors={filteredErrors} /> : null
}

type PageWithGridProps = {
  children?: ReactNode
  className?: string
  isLoading?: boolean
  loadingMessage?: ReactNode
  errorsToHandle?: Array<HandledError>
  errorHandler?: (error?: HandledError) => string
}

type HandledError = {
  type: string
  code?: string | number
  httpStatusCode?: number
  // TODO add the "error shape"
  data: any
}

const PageWithGrid = ({
  children,
  className = '',
  isLoading,
  loadingMessage = <FormattedMessage {...messages.loading} />,
  errorsToHandle // Deprecated
}: PageWithGridProps) => {
  const errors = useAppSelector(getErrors)
  const error = errors.length > 0 && errorsToHandle?.length && handleErrors(errorsToHandle, errors)

  return (
    <StyledPageWrapper className={className}>
      <StyledContent style={{ border: 'none' }}>
        {error ? (
          <div className="error">{error}</div>
        ) : isLoading ? (
          <Loading isLoading={isLoading} loadingMessage={loadingMessage} />
        ) : (
          children
        )}
      </StyledContent>
    </StyledPageWrapper>
  )
}

export default PageWithGrid
